import { combineEpics } from 'redux-observable';
import { filter, map, mapTo, merge, mergeMap, of } from 'rxjs';
import { isOfType } from 'typesafe-actions';
import { catchAxiosError, catchUnauthorizedError, createEpic$ } from 'lib/store';
import { notificationError, notificationInfo } from 'store/notifications';
import { systemUnauthorized } from 'store/system';
import { systemTransactionSelect } from 'store/transactions';
import {
  systemTransactionArchiveResponse,
  systemTransactionArchiveResult,
  SystemTransactionHandleAction,
  systemTransactionHandleError,
  systemTransactionRestoreResponse,
} from './actions';

const systemTransactionsArchiveEpic$ = createEpic$(({ action$, archiveTransaction$ }) =>
  action$.pipe(
    filter(isOfType(SystemTransactionHandleAction.ArchiveRequest)),
    mergeMap(action =>
      archiveTransaction$({
        systemTransactions: action.payload,
      }).pipe(
        mergeMap(response =>
          merge(
            of(systemTransactionArchiveResponse(response)),
            of(systemTransactionArchiveResult({ ...response, systemTransactions: action.payload })),
          ),
        ),
        catchAxiosError(systemTransactionHandleError),
        catchUnauthorizedError(systemUnauthorized),
      ),
    ),
  ),
);

const systemTransactionsArchiveSuccessEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(SystemTransactionHandleAction.ArchiveResponse)),
    mapTo(
      notificationInfo({
        caption: 'Success',
        message: `System transactions successfully archived`,
      }),
    ),
  ),
);

const systemTransactionsClearSelectionEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(
      isOfType([
        SystemTransactionHandleAction.ArchiveResponse,
        SystemTransactionHandleAction.RestoreResponse,
      ]),
    ),
    mapTo(systemTransactionSelect([])),
  ),
);

const systemTransactionHandleErrorEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(SystemTransactionHandleAction.Error)),
    mapTo(
      notificationError({
        caption: 'Error',
        duration: 10000,
        message: 'There was a problem processing system transactions',
      }),
    ),
  ),
);

const systemTransactionsRestoreEpic$ = createEpic$(({ action$, restoreTransaction$ }) =>
  action$.pipe(
    filter(isOfType(SystemTransactionHandleAction.RestoreRequest)),
    mergeMap(action =>
      restoreTransaction$({
        mappedTransactionMappingIds: action.payload,
      }).pipe(
        map(() => systemTransactionRestoreResponse(action.payload)),
        catchAxiosError(systemTransactionHandleError),
        catchUnauthorizedError(systemUnauthorized),
      ),
    ),
  ),
);

const systemTransactionsRestoreSuccessEpic$ = createEpic$(({ action$ }) =>
  action$.pipe(
    filter(isOfType(SystemTransactionHandleAction.RestoreResponse)),
    mapTo(
      notificationInfo({
        caption: 'Success',
        message: `System transactions successfully restored`,
      }),
    ),
  ),
);

export default combineEpics(
  systemTransactionsArchiveEpic$,
  systemTransactionsArchiveSuccessEpic$,
  systemTransactionsClearSelectionEpic$,
  systemTransactionHandleErrorEpic$,
  systemTransactionsRestoreEpic$,
  systemTransactionsRestoreSuccessEpic$,
);
